import React, { useContext, useEffect, useState } from "react";
import {
  Col,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Button,
  Modal,
  message,
  InputNumber,
} from "antd";
import { OrderProps } from "../../../interfaces/interfaces";
import DataContext from "../../../context/DataContext";

interface Props {
  values: OrderProps;
  setValues: React.Dispatch<React.SetStateAction<OrderProps>>;
  form: FormInstance<any>;
  onClickField: () => void;
}

export const OrderForm = ({ values, setValues, form, onClickField }: Props) => {
  const { rigNumbers, handleCreateRig, orderData } = useContext(DataContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRigName, setNewRigName] = useState("");

  useEffect(() => {
    form.setFieldsValue(values);
  }, [form, values]);

  useEffect(() => {
    let orderIdNum = orderData.length + 1;
    let orderId = orderIdNum.toString().padStart(4, "0");
    setValues((prev) => ({
      ...prev,
      OrderId: orderId,
    }));
  }, [orderData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    onClickField();
    setValues((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleRigSelect = (value: number) => {
    setValues((prev) => ({
      ...prev,
      RigNumber: value,
    }));
  };

  const handleOpenModal = () => {
    setNewRigName("");
    setIsModalOpen(true);
  };

  const createRig = async () => {
    if (!newRigName.trim()) {
      message.error("Please enter a Rig Number name.");
      return;
    }

    try {
      handleCreateRig(newRigName);
      setIsModalOpen(false);
    } catch (error) {
      message.error("Failed to create Rig Number. Please try again.");
    }
  };

  const filterOption = (
    input: string,
    option?: { Name: string; value: string },
  ) => {
    return ((option && option.Name) || "")
      .toLowerCase()
      .includes(input.toLowerCase());
  };

  return (
    <Form name="order" layout="vertical" initialValues={values} form={form}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="OrderId"
            label="Order ID"
            rules={[{ required: true, message: "Please input Order ID" }]}
          >
            <Input
              disabled
              name="OrderId"
              placeholder="Order ID"
              value={values.OrderId || ""}
              onChange={handleChange}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Rig Number"
            name="RigNumber"
            rules={[
              {
                required: true,
                message: "Please select a Rig Number",
              },
              ({}) => ({
                validator(_, value) {
                  if (value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Please select a Rig Number"),
                  );
                },
              }),
            ]}
          >
            <Row gutter={8}>
              <Col flex="auto">
                <Select
                  showSearch
                  placeholder="Select a Rig Number"
                  filterOption={filterOption}
                  value={values.RigNumber}
                  onChange={handleRigSelect}
                  style={{ width: "100%" }}
                  fieldNames={{ label: "Name", value: "OID" }}
                  options={rigNumbers as any}
                ></Select>
              </Col>
              <Col>
                <Button type="primary" onClick={handleOpenModal}>
                  New
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item label="Connection" name="Connection">
            <Input
              name="Connection"
              placeholder="Connection"
              value={values.Connection}
              style={{ width: "100%" }}
              onChange={handleChange}
            />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Pin Recuts" name="PinRecuts">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              formatter={(value) =>
                value ? value.toString().replace(",", ".") : ""
              }
              parser={(value) =>
                parseFloat(value ? value.replace(",", ".") : "")
              }
              name="PinRecuts"
              placeholder="PinRecuts"
              value={values.PinRecuts}
              onChange={(newValue) => {
                if (newValue)
                  setValues((prev) => {
                    return {
                      ...prev,
                      PinRecuts: newValue,
                    };
                  });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Box Recuts" name={"BoxRecuts"}>
            <InputNumber
              min={0}
              name="BoxRecuts"
              style={{ width: "100%" }}
              formatter={(value) =>
                value ? value.toString().replace(",", ".") : ""
              }
              parser={(value) =>
                parseFloat(value ? value.replace(",", ".") : "")
              }
              placeholder="BoxRecuts"
              value={values.BoxRecuts}
              onChange={(newValue) => {
                if (newValue)
                  setValues((prev) => {
                    return {
                      ...prev,
                      BoxRecuts: newValue,
                    };
                  });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Pin Reface" name="PinReface">
            <InputNumber
              name="PinReface"
              min={0}
              style={{ width: "100%" }}
              formatter={(value) =>
                value ? value.toString().replace(",", ".") : ""
              }
              parser={(value) =>
                parseFloat(value ? value.replace(",", ".") : "")
              }
              placeholder="Pin Reface"
              value={values.PinReface}
              onChange={(newValue) => {
                if (newValue)
                  setValues((prev) => {
                    return {
                      ...prev,
                      PinReface: newValue,
                    };
                  });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item label="Box Reface" name="BoxReface">
            <InputNumber
              name="BoxReface"
              min={0}
              style={{ width: "100%" }}
              formatter={(value) =>
                value ? value.toString().replace(",", ".") : ""
              }
              parser={(value) =>
                parseFloat(value ? value.replace(",", ".") : "")
              }
              placeholder="Box Reface"
              value={values.BoxReface}
              onChange={(newValue) => {
                if (newValue)
                  setValues((prev) => {
                    return {
                      ...prev,
                      BoxReface: newValue,
                    };
                  });
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Add Rig Modal */}
      <Modal
        title="Add New Rig Number"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="create" type="primary" onClick={createRig}>
            Create
          </Button>,
        ]}
      >
        <Input
          placeholder="Enter Rig Number name"
          value={newRigName}
          onChange={(e) => setNewRigName(e.target.value)}
        />
      </Modal>
    </Form>
  );
};
